import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { loadViewerDocument, renderPageToCanvas, type PDFDocumentProxy } from '../lib/pdf-render.js';
import { useEditorStore } from '../lib/editor-store.js';
import { effectiveRotations } from '../lib/effective-rotation.js';
import { effectiveOrder } from '../lib/effective-order.js';
import { scrollToPageIndex } from '../lib/scroll-to-page.js';
import { parsePageRange } from '../lib/page-range.js';

/*
 * Page navigation rail — one mini canvas per page, in the current display order
 * (reorder-aware). Loads its own pdf.js document instance (cheap; pdf.js shares
 * the worker). Click a thumb to scroll the matching page into view. Drag a thumb
 * onto another to reorder: that queues a reorder-page edit using the dragged and
 * target DISPLAY positions, which is exactly what the driver's movePage(from,to)
 * and the effectiveOrder fold consume.
 */

const THUMB_SCALE = 0.18;

function Thumb({
  doc,
  page,
  pos,
  rotation,
  pendingRemoval,
  selected,
  dragOver,
  onClick,
  onDragStart,
  onDragEnter,
  onDrop,
  onDragEnd,
}: {
  doc: PDFDocumentProxy;
  page: { index: number };
  pos: number;
  rotation: number;
  pendingRemoval: boolean;
  selected: boolean;
  dragOver: boolean;
  onClick: (e: MouseEvent<HTMLButtonElement>) => void;
  onDragStart: () => void;
  onDragEnter: () => void;
  onDrop: () => void;
  onDragEnd: () => void;
}) {
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
  useEffect(() => {
    if (canvas) void renderPageToCanvas(doc, page.index + 1, canvas, THUMB_SCALE, rotation).catch(() => undefined);
  }, [canvas, doc, page.index, rotation]);
  const moved = pos !== page.index;
  return (
    <button
      type="button"
      className={`thumb ${pendingRemoval ? 'thumb--pending-remove' : ''} ${selected ? 'thumb--selected' : ''} ${dragOver ? 'thumb--drop' : ''}`.trim().replace(/\s+/g, ' ')}
      aria-pressed={selected}
      onClick={onClick}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        onDragStart();
      }}
      onDragOver={(e) => e.preventDefault()}
      onDragEnter={onDragEnter}
      onDrop={(e) => {
        e.preventDefault();
        onDrop();
      }}
      onDragEnd={onDragEnd}
      aria-label={`P${pos + 1}${moved ? ` (元P${page.index + 1})` : ''} — クリックで選択・表示 / Shift・⌘で複数選択 / ドラッグで並べ替え`}
      title={`P${pos + 1}${pendingRemoval ? ' (削除予定)' : ''}${selected ? ' (選択中)' : ''}`}
    >
      <canvas ref={setCanvas} className="thumb__canvas" />
      <span className="thumb__label">{pos + 1}</span>
    </button>
  );
}

export function PageThumbnails() {
  const originalBytes = useEditorStore((s) => s.originalBytes);
  const pages = useEditorStore((s) => s.doc?.pages ?? []);
  const edits = useEditorStore((s) => s.edits);
  const addEdit = useEditorStore((s) => s.addEdit);
  const selectedPageIndices = useEditorStore((s) => s.selectedPageIndices);
  const setPageSelection = useEditorStore((s) => s.setPageSelection);
  const togglePageSelection = useEditorStore((s) => s.togglePageSelection);
  const clearPageSelection = useEditorStore((s) => s.clearPageSelection);
  const rotatePages = useEditorStore((s) => s.rotatePages);
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragOverPos, setDragOverPos] = useState<number | null>(null);
  // Display position of the last plain/cmd click — the anchor for Shift-range select.
  const lastClickPos = useRef<number | null>(null);
  const rotations = effectiveRotations(pages, edits);
  const order = effectiveOrder(pages.length, edits);
  const selectedSet = new Set(selectedPageIndices);

  useEffect(() => {
    if (!originalBytes) {
      setDoc(null);
      return;
    }
    let active = true;
    let loaded: PDFDocumentProxy | null = null;
    loadViewerDocument(originalBytes)
      .then((d) => {
        if (active) {
          loaded = d;
          setDoc(d);
        } else void d.destroy();
      })
      .catch(() => undefined);
    return () => {
      active = false;
      void loaded?.destroy();
    };
  }, [originalBytes]);

  if (pages.length === 0) return null;

  function endDrag(): void {
    setDragFrom(null);
    setDragOverPos(null);
  }

  function dropOn(targetPos: number): void {
    if (dragFrom !== null && dragFrom !== targetPos) {
      addEdit({ kind: 'reorder-page', from: dragFrom, to: targetPos });
    }
    endDrag();
  }

  // Click selects a page (plain = replace + scroll into view; Shift = range from
  // the last anchor; Cmd/Ctrl = toggle). Selection drives the bulk-rotate bar.
  function onThumbClick(e: MouseEvent<HTMLButtonElement>, origIdx: number, pos: number): void {
    if (e.shiftKey && lastClickPos.current !== null) {
      const a = Math.min(lastClickPos.current, pos);
      const b = Math.max(lastClickPos.current, pos);
      setPageSelection(order.slice(a, b + 1));
    } else if (e.metaKey || e.ctrlKey) {
      togglePageSelection(origIdx);
      lastClickPos.current = pos;
    } else {
      setPageSelection([origIdx]);
      lastClickPos.current = pos;
      scrollToPageIndex(origIdx);
    }
  }

  const nSel = selectedPageIndices.length;
  return (
    <aside className="thumbs" aria-label="ページ一覧">
      {doc && (
        <div className="thumbs__bar" role="toolbar" aria-label="ページ回転">
          <span className="thumbs__count">{nSel > 0 ? `${nSel} 選択` : '回転'}</span>
          <button type="button" className="thumbs__rbtn" title="選択ページを左90°回転" disabled={nSel === 0} onClick={() => rotatePages(selectedPageIndices, -90)}>⟲</button>
          <button type="button" className="thumbs__rbtn" title="選択ページを右90°回転" disabled={nSel === 0} onClick={() => rotatePages(selectedPageIndices, 90)}>⟳</button>
          <button type="button" className="thumbs__rbtn" title="選択ページを180°回転" disabled={nSel === 0} onClick={() => rotatePages(selectedPageIndices, 180)}>180°</button>
          <button type="button" className="thumbs__rbtn" title="全ページを選択" onClick={() => setPageSelection(pages.map((p) => p.index))}>全</button>
          {nSel > 0 && (
            <button type="button" className="thumbs__rbtn" title="選択を解除" onClick={clearPageSelection}>×</button>
          )}
          <input
            className="thumbs__range"
            type="text"
            placeholder="例 1,3,5-8"
            aria-label="ページ範囲を指定して選択"
            title="ページ範囲を指定（例 1,3,5-8）"
            onChange={(e) => setPageSelection(parsePageRange(e.target.value, pages.length))}
          />
        </div>
      )}
      {!doc && <p className="thumbs__loading">読み込み中…</p>}
      {doc &&
        order.map((origIdx, pos) => {
          const p = pages[origIdx];
          if (!p) return null;
          const rotation = rotations.get(p.index) ?? p.rotation;
          const pendingRemoval = edits.some((e) => e.kind === 'remove-page' && e.page === p.index);
          return (
            <Thumb
              key={p.index}
              doc={doc}
              page={p}
              pos={pos}
              rotation={rotation}
              pendingRemoval={pendingRemoval}
              selected={selectedSet.has(p.index)}
              dragOver={dragOverPos === pos && dragFrom !== null && dragFrom !== pos}
              onClick={(e) => onThumbClick(e, p.index, pos)}
              onDragStart={() => setDragFrom(pos)}
              onDragEnter={() => setDragOverPos(pos)}
              onDrop={() => dropOn(pos)}
              onDragEnd={endDrag}
            />
          );
        })}
    </aside>
  );
}
